import {useState} from 'react';
import {CircleCheckIcon} from 'lucide-react';
import {useStaticAssetsUrl} from '@tinct/core-utils';
import {
  Button,
  Checkbox,
  CopyButton,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
  Label,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@tinct/web-components';

interface LaunchLandingPageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Where the page will live once published (landingPage.publishedUrl). */
  publishedUrl: string | null;
  /** Custom-domain URL — set when the workspace hosting domain is active. */
  organizationPublishedUrl?: string | null;
  isPublishing: boolean;
  /** Switches the modal to its post-launch confirmation view. */
  launched?: boolean;
  onLaunch: () => void;
  /** Post-launch: continue into the variant phase (audience step). */
  onCreateVariants?: () => void;
  /** Post-launch: exit to the campaign details page. */
  onGoToCampaign?: () => void;
}

function LiveUrlRow({url}: {url: string}) {
  return (
    // Block flow + break-all (not flex + truncate): the URL must WRAP, never
    // widen the dialog — an unbreakable line here blows past max-w-md.
    <div className="flex w-full min-w-0 items-start justify-center gap-1">
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="min-w-0 break-all text-sm font-medium text-foreground underline underline-offset-2 hover:text-muted-foreground"
      >
        {url}
      </a>
      <CopyButton value={url} className="shrink-0" />
    </div>
  );
}

/**
 * NEW-origin variant of LaunchCampaignModal: publishing is a synchronous
 * storage copy of the hosted page — no variant counts, no publishing step.
 * After a successful launch the modal flips to a confirmation view: the live
 * URL(s), the "nothing to install" reassurance, and the hand-off into the
 * variant phase.
 */
export const LaunchLandingPageModal = ({
  open,
  onOpenChange,
  publishedUrl,
  organizationPublishedUrl,
  isPublishing,
  launched = false,
  onLaunch,
  onCreateVariants,
  onGoToCampaign,
}: LaunchLandingPageModalProps) => {
  const assetsUrl = useStaticAssetsUrl();
  const [confirmed, setConfirmed] = useState(false);

  const handleOpenChange = (value: boolean) => {
    onOpenChange(value);
    if (!value) {
      setConfirmed(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton
        className="max-w-md gap-0 overflow-hidden rounded-3xl p-0"
      >
        {/* Cover image */}
        <div className="px-2 pt-2">
          <div className="overflow-hidden rounded-2xl">
            <img
              src={`${assetsUrl}/launch-campaign.png`}
              alt=""
              className="aspect-[2/1] w-full object-cover"
            />
          </div>
        </div>

        {launched ? (
          <>
            {/* Post-launch confirmation */}
            <div className="flex min-w-0 flex-col gap-3 p-8 pb-4 text-center">
              <div className="flex items-center justify-center gap-2">
                <CircleCheckIcon className="size-6 text-teal-600" />
                <DialogTitle className="font-heading text-2xl font-semibold tracking-[-0.4px] text-foreground">
                  Your page is live
                </DialogTitle>
              </div>
              {publishedUrl && <LiveUrlRow url={publishedUrl} />}
              {organizationPublishedUrl && (
                <LiveUrlRow url={organizationPublishedUrl} />
              )}
              <p className="text-sm text-muted-foreground">
                Nothing to install — Tinct hosts and serves this page. Next,
                create a personalized variant for each target account.
              </p>
            </div>

            <DialogFooter className="flex-row gap-2 px-8 pb-8 pt-4">
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                onClick={onGoToCampaign}
              >
                Go to campaign
              </Button>
              <Button size="lg" className="flex-1" onClick={onCreateVariants}>
                Create page variants
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            {/* Content */}
            <div className="flex flex-col gap-3 p-8 text-center">
              <DialogTitle className="font-heading text-2xl font-semibold tracking-[-0.4px] text-foreground">
                Ready to launch?
              </DialogTitle>
              <p className="text-base text-muted-foreground">
                {publishedUrl ? (
                  <>
                    Your page will go live at{' '}
                    <span className="break-all font-medium text-foreground">{publishedUrl}</span>
                  </>
                ) : (
                  'Your page will go live on its Tinct address.'
                )}
              </p>
            </div>

            {/* Checkbox confirmation */}
            <div className="flex items-start gap-2 px-8 pb-6">
              <Checkbox
                id="confirm-launch-lp"
                checked={confirmed}
                onCheckedChange={(checked) => setConfirmed(checked === true)}
              />
              <Label
                htmlFor="confirm-launch-lp"
                className="cursor-pointer text-sm font-medium leading-none text-foreground"
              >
                I&apos;ve reviewed my page and yes it is ready !
              </Label>
            </div>

            {/* Footer */}
            <DialogFooter className="flex-row gap-2 px-8 pb-8">
              <Button
                variant="outline"
                size="lg"
                className="flex-1"
                onClick={() => handleOpenChange(false)}
                disabled={isPublishing}
              >
                Cancel
              </Button>
              <Tooltip>
                <TooltipTrigger asChild>
                  {/* span keeps hover events alive while the button is disabled */}
                  <span className={`flex-1 ${!confirmed ? 'cursor-not-allowed' : ''}`}>
                    <Button
                      size="lg"
                      className="w-full"
                      onClick={onLaunch}
                      disabled={isPublishing || !confirmed}
                    >
                      {isPublishing ? 'Launching...' : 'Launch'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {!confirmed && (
                  <TooltipContent>
                    Check the review box above to launch
                  </TooltipContent>
                )}
              </Tooltip>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
